/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { NotifyProps } from "@eisinc/App/Components/Notify";
import { LazyImageStyle } from "@eisinc/MobiusOverrides/LazyImage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface MiniCartOverlayStyles {
    miniCartContainerStyles?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    miniCartHeadingStyles?: InjectableCss;
    msgWrapper?: InjectableCss;
    notificationContainers?: NotifyProps;
    miniCartHeadingTotalStyles?: InjectableCss;
    cartLinesHeadingStyles?: InjectableCss;
    fancyOverflowStyles?: InjectableCss;
    cartLineStyles?: InjectableCss;
    cartLineRightSideStyles?: InjectableCss;
    cartLineRemoveButtonStyles?: InjectableCss;
    cartLineImageStyles?: InjectableCss;
    cartLineDescriptionNumbersWrapper?: InjectableCss;
    cartLineDescriptionStyles?: InjectableCss;
    cartLineNumbersStyles?: InjectableCss;
    cartLineLeftSideStyles?: InjectableCss;
    cartLineLeftSideStylesWrapper?: InjectableCss;
    cartLineOrderedQtyWrapper?: InjectableCss;
    cartLinePriceTotalWrapper?: InjectableCss;
    minicartActionButtonsStyles?: InjectableCss;
    minicartCheckoutButton?: InjectableCss;
}

export const minicartStyles: MiniCartOverlayStyles = {
    miniCartContainerStyles: {
        css: css`
            width: 640px;
            max-height: 715px;
            min-height: 200px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        `,
    },
    miniCartHeadingStyles: {
        css: css`
            padding: 15px 15px 8px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;

            & h3 {
                font-size: 24px;
                line-height: 29px;
            }
        `,
    },
    msgWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            display: flex;
            flex-direction: column;
            gap: 10px;
        `,
    },
    notificationContainers: {
        notifyContainerOverrides: css`
            max-width: 100%;
            margin-bottom: 0;
        `,
    },
    miniCartHeadingTotalStyles: {
        css: css`
            width: 43%;
            display: flex;
            justify-content: space-between;
            & strong,
            & span {
                font-size: 15px;
                line-height: 18px;
            }
            & .label:after {
                content: ":";
            }
        `,
    },
    cartLinesHeadingStyles: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: ${getColor("common.ui3")};

            & span {
                font-size: 12px;
                line-height: 18px;
                color: #333333;
                text-transform: uppercase;
            }

            & .description {
                width: 57%;
                text-align: left;
            }

            & .qty {
                width: 100px;
                text-align: left;
            }

            & .price {
                flex-grow: 1;
                display: flex;
                justify-content: space-between;

                span {
                    display: block;
                    width: 50%;
                }
                span.total {
                    text-align: center;
                }
            }
        `,
    },
    fancyOverflowStyles: {
        css: css`
            max-height: 500px;
            min-height: 108px;
            padding: 0 15px;
            box-sizing: border-box;
            overflow-y: auto;
            overflow-x: hidden;

            &&::-webkit-scrollbar-track {
                border: 1px solid #c3c3c3;
                background-color: white;
            }

            &&::-webkit-scrollbar-button {
                display: none;
            }

            &&::-webkit-scrollbar {
                background-color: white;
                border-radius: 3px;
                width: 8px;
            }

            &&::-webkit-scrollbar-thumb {
                background-color: #bebebe;
                border-radius: 3px;
                width: 6px;
            }

            &.empty-cart {
                display: flex;
                justify-content: center;
                align-items: center;
            }

            @media (max-height: 800px) {
                max-height: 200px;
            }
        `,
    },
    spinnerWrapper: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;

            &:before {
                content: "";
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background: ${getColor("primary.contrast")};
                opacity: 0.75;
                z-index: 1;
            }

            & ${/* sc-selector */ LoadingSpinnerStyle} {
                z-index: 2;
            }
        `,
    },
    cartLineStyles: {
        css: css`
            width: 100%;
            padding: 15px 0;
            position: relative;
            display: flex;
            border-bottom: 1px solid #c3c3c3;

            &:last-child {
                border-bottom: 0;
            }

            &:hover .remove,
            &:focus .remove {
                display: block;
                outline: 0;

                & svg g {
                    fill: ${getColor("common.ui6")};
                }
            }
        `,
    },
    cartLineRightSideStyles: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 57%;
        `,
    },
    cartLineRemoveButtonStyles: {
        css: css`
            position: absolute;
            right: 0;
            top: -2px;
            background: transparent;
            padding: 0;
            display: none;
            border: 0;
            height: 20;

            &:hover,
            &:focus {
                outline: 0;
                background: transparent;
                border: 0;

                & svg g {
                    fill: ${getColor("danger.main")} !important;
                }
            }
        `,
    },
    cartLineImageStyles: {
        css: css`
            height: 75px;
            width: 80px;
            margin-right: 10px;

            ${/* sc-selector */ LazyImageStyle} {
                width: 100%;
                position: relative;

                & img {
                    width: auto;
                    height: auto;
                    max-width: 75px;
                    max-height: 75px;
                }
            }
        `,
    },
    cartLineDescriptionNumbersWrapper: {
        css: css`
            width: calc(100% - 90px);
            padding-right: 10px;
        `,
    },
    cartLineDescriptionStyles: {
        css: css`
            display: block;
            font-size: 14px;
            line-height: 16px;
            margin-bottom: 15px;
            max-height: 48px;
            min-height: 30px;
            overflow: hidden;
            color: ${getColor("common.ui6")};
            &:hover,
            &:focus {
                text-decoration: underline;
                outline: 0;
            }
        `,
    },
    cartLineNumbersStyles: {
        css: css`
            & div {
                font-size: 14px;
                line-height: 16px;
            }
            & strong,
            & span {
                font-size: 12px;
                line-height: 14px;
            }
        `,
    },
    cartLineLeftSideStyles: {
        css: css`
            width: 43%;
            padding-top: 10px;

            & .error-message {
                margin-top: 5px;
                font-size: 13px;
            }
        `,
    },
    cartLineLeftSideStylesWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    cartLineOrderedQtyWrapper: {
        css: css`
            &.qty * {
                margin-top: 0 !important;
                max-height: 100%;
            }
            width: 110px;
            & input {
                max-width: 46px;
                height: 32px !important;
                text-align: center;
                padding: 0 !important;
            }
            & input::-webkit-outer-spin-button,
            & input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            & input[type="number"] {
                -moz-appearance: textfield !important;
            }

            & input:focus {
                border: 2px solid ${getColor("text.main")} !important;
            }
        `,
    },
    cartLinePriceTotalWrapper: {
        css: css`
            width: calc(100% - 83px);
            display: flex;
            justify-content: space-between;

            & span {
                width: 50%;
                font-size: 12px;
                line-height: 18px;
            }

            & span.total {
                padding-left: 16px;
                word-break: break-all;
            }

            & span.quote {
                width: 100%;
                text-align: center;
                font-size: 14px;
                padding-right: 30px;
            }
        `,
    },
    minicartActionButtonsStyles: {
        css: css`
            padding: 15px;
            padding-top: 17px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: ${getColor("common.ui3")};
            width: 100%;

            & button,
            & a {
                min-height: 32px;
                height: 32px;
                font-size: 14px;
                line-height: 28px;
                min-width: 150px;
            }
        `,
    },
    minicartCheckoutButton: {
        css: css`
            margin-left: 50px;
        `,
    },
};
